import type { ReactNode } from 'react'
import { Button } from './Button.js'

/**
 * Full-screen error layout. Used for: failed auth, failed session start,
 * failed results fetch, scoring errors. Before this existed each screen
 * rendered its own slightly-different "something went wrong" block — same
 * shape, drift in spacing, sometimes a button, sometimes not.
 *
 * Always shows the same composition:
 *  - red circular icon (or a bang fallback)
 *  - bold heading
 *  - muted message
 *  - optional primary action
 *  - optional subtle secondary link
 */

interface ErrorStateProps {
  title: string
  message?: string | null
  primaryAction?: { label: string; onClick: () => void }
  secondaryAction?: { label: string; onClick: () => void }
  /** Custom icon, defaults to a red circle with a bang. */
  icon?: ReactNode
}

export function ErrorState({
  title,
  message,
  primaryAction,
  secondaryAction,
  icon,
}: ErrorStateProps) {
  return (
    <div className="flex flex-col items-center text-center max-w-sm mx-auto gap-3 animate-fade-in">
      <div
        className="w-14 h-14 rounded-full bg-brand-error-soft text-brand-error flex items-center justify-center text-2xl font-bold"
        aria-hidden
      >
        {icon ?? '!'}
      </div>
      <h2 className="text-lg font-semibold text-brand-text">{title}</h2>
      {message && (
        <p className="text-sm text-brand-text-muted leading-relaxed">
          {message}
        </p>
      )}
      {(primaryAction || secondaryAction) && (
        <div className="flex flex-col gap-2 w-full mt-2">
          {primaryAction && (
            <Button onClick={primaryAction.onClick}>
              {primaryAction.label}
            </Button>
          )}
          {secondaryAction && (
            <Button
              variant="subtle"
              size="sm"
              onClick={secondaryAction.onClick}
            >
              {secondaryAction.label}
            </Button>
          )}
        </div>
      )}
    </div>
  )
}
